"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import {
  fetchMatchDisputesByMatch,
  fetchMatchDisputesByTournament,
  fetchMatchDisputeById,
  createMatchDispute,
  resolveMatchDispute,
  rejectAllMatchDisputes,
} from "./client";
import type {
  CreateMatchDisputeRequest,
  ResolveMatchDisputeRequest,
} from "../model/types";
import { handleError } from "@/shared/api/error-handler";

export const matchDisputeQueryKeys = {
  all: ["match-disputes"] as const,
  byMatch: (matchId: string) =>
    [...matchDisputeQueryKeys.all, "match", matchId] as const,
  byTournament: (tournamentId: string) =>
    [...matchDisputeQueryKeys.all, "tournament", tournamentId] as const,
  byId: (id: string) => [...matchDisputeQueryKeys.all, "id", id] as const,
};

/**
 * `enabled` — чтобы вызывающий не тянул диспуты там, где их не бывает.
 * Диспут заводится только по завершённому матчу, а страница матча запрашивала
 * список всегда, включая ещё не сыгранные матчи.
 */
export function useMatchDisputesByMatch(
  matchId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: matchDisputeQueryKeys.byMatch(matchId),
    queryFn: () => fetchMatchDisputesByMatch(matchId),
    enabled: Boolean(matchId) && (options?.enabled ?? true),
  });
}

export function useMatchDisputesByTournament(tournamentId: string) {
  return useQuery({
    queryKey: matchDisputeQueryKeys.byTournament(tournamentId),
    queryFn: () => fetchMatchDisputesByTournament(tournamentId),
    enabled: Boolean(tournamentId),
  });
}

export function useMatchDispute(id: string | null) {
  return useQuery({
    queryKey: matchDisputeQueryKeys.byId(id ?? ""),
    queryFn: () => fetchMatchDisputeById(id!),
    enabled: Boolean(id),
  });
}

export function useCreateMatchDispute(matchId: string, tournamentId: string) {
  const tToast = useTranslations("Toasts");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMatchDisputeRequest) => createMatchDispute(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: matchDisputeQueryKeys.byMatch(matchId),
      });
      queryClient.invalidateQueries({
        queryKey: matchDisputeQueryKeys.byTournament(tournamentId),
      });
      toast.success(tToast("hooks.disputSozdan"));
    },
    onError: (error) => {
      handleError(error, { component: "CreateMatchDispute", action: "create" });
    },
  });
}

/**
 * KAN-171: массовое отклонение неразобранных диспутов турнира.
 * Ошибку не тостим здесь: вызывающий показывает текст 400 прямо в модалке
 * («сначала разберите N конфликтов результатов») — это часть флоу, а не сбой.
 */
export function useRejectAllMatchDisputes(tournamentId: string) {
  const tUi = useTranslations("Ui");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resolutionComment: string) =>
      rejectAllMatchDisputes(tournamentId, { resolutionComment }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: matchDisputeQueryKeys.byTournament(tournamentId),
      });
      toast.success(
        result.closedCount > 0
          ? tUi("litHooks.zakrytoDisputov", { count: result.closedCount })
          : tUi("litHooks.nerazobrannyhDisputovNeOstalos")
      );
    },
  });
}

export function useResolveMatchDispute(
  disputeId: string,
  tournamentId: string,
  matchId: string
) {
  const tToast = useTranslations("Toasts");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ResolveMatchDisputeRequest) =>
      resolveMatchDispute(disputeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: matchDisputeQueryKeys.byId(disputeId),
      });
      queryClient.invalidateQueries({
        queryKey: matchDisputeQueryKeys.byTournament(tournamentId),
      });
      queryClient.invalidateQueries({
        queryKey: matchDisputeQueryKeys.byMatch(matchId),
      });
      toast.success(tToast("hooks.disputRassmotren"));
    },
    onError: (error) => {
      handleError(error, { component: "ResolveMatchDispute", action: "resolve" });
    },
  });
}
